import { useState, useEffect } from "react";
import Layout from "../../components/Layout/Layout";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeftIcon,
  CurrencyDollarIcon,
  SwitchHorizontalIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  InformationCircleIcon,
} from "@heroicons/react/outline";
import ThirdPartyServices from "../../services/ThirdParty";
import { HollowDotsSpinner } from "react-epic-spinners";
import fire from "../../utils/Toastr";
import { FormatMoney } from "../../utils/formatMoney";

const statusBadge = (status) => {
  const styles = {
    Queued: "bg-blue-100 text-blue-800",
    Pending: "bg-yellow-100 text-yellow-800",
    Processing: "bg-indigo-100 text-indigo-800",
    Completed: "bg-green-100 text-green-800",
    Failed: "bg-red-100 text-red-800",
  };
  return styles[status] || "bg-gray-100 text-gray-800";
};

export default function ViewPayrollTransaction() {
  const { tenantId, userId, payrollTransactionId } = useParams();
  const [tx, setTx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (tenantId && userId && payrollTransactionId) {
      fetchTransaction();
    }
  }, [tenantId, userId, payrollTransactionId]);

  const fetchTransaction = async () => {
    setLoading(true);
    try {
      const response = await ThirdPartyServices.viewPayrollTransaction(
        tenantId,
        userId,
        payrollTransactionId,
      );

      if (response.status && response.data) {
        setTx(response.data);
      } else {
        setError(response.message || "Transaction not found");
      }
    } catch (err) {
      setError("Failed to fetch transaction details");
      fire("error", "Failed to fetch transaction details");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Loading..." current="thirdparty" pageTag="thirdparty">
        <div className="flex justify-center items-center h-64">
          <HollowDotsSpinner color="#4A90E2" />
        </div>
      </Layout>
    );
  }

  if (error || !tx) {
    return (
      <Layout
        title="Transaction Not Found"
        current="thirdparty"
        pageTag="thirdparty"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <CurrencyDollarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Transaction Not Found
            </h3>
            <p className="text-gray-500 mb-4">
              {error || "The requested transaction could not be found."}
            </p>
            <Link
              to="/thirdparty/payroll-transactions"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Transactions
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const SectionCard = ({ title, icon: Icon, children }) => (
    <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center">
          {Icon && <Icon className="h-5 w-5 text-gray-500 mr-2" />}
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        </div>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  );

  const DetailRow = ({ label, value }) => (
    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-4">
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
        {value ?? "N/A"}
      </dd>
    </div>
  );

  return (
    <Layout
      title="Payroll Transaction Details"
      current="thirdparty"
      pageTag="thirdparty"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Link
          to="/thirdparty/payroll-transactions"
          className="inline-flex items-center text-sm text-red-600 hover:text-red-500 mb-6"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Payroll Transactions
        </Link>

        <div className="bg-gradient-to-r from-red-600 to-red-800 rounded-lg px-8 py-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="h-14 w-14 rounded-full bg-white/20 flex items-center justify-center">
                <CurrencyDollarIcon className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  Payroll Transaction
                </h2>
                <p className="text-red-100 text-sm mt-1">
                  {tx.transferType} Transfer &middot;{" "}
                  {FormatMoney(tx.amount)}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusBadge(tx.processStatus)}`}
              >
                {tx.processStatus}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <SectionCard title="Source Account" icon={SwitchHorizontalIcon}>
            <dl className="divide-y divide-gray-200">
              <DetailRow label="Account Name" value={tx.sourceAccountName} />
              <DetailRow
                label="Account Number"
                value={tx.sourceAccountNumber}
              />
              <DetailRow label="Bank Code" value={tx.sourceBankCode} />
            </dl>
          </SectionCard>

          <SectionCard title="Destination Account" icon={SwitchHorizontalIcon}>
            <dl className="divide-y divide-gray-200">
              <DetailRow
                label="Account Name"
                value={tx.destinationAccountName}
              />
              <DetailRow
                label="Account Number"
                value={tx.destinationAccountNumber}
              />
              <DetailRow label="Bank Code" value={tx.destinationBankCode} />
            </dl>
          </SectionCard>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <SectionCard title="Transaction Details" icon={InformationCircleIcon}>
            <dl className="divide-y divide-gray-200">
              <DetailRow label="Transaction ID" value={tx.id} />
              <DetailRow label="Transfer Type" value={tx.transferType} />
              <DetailRow label="Amount" value={FormatMoney(tx.amount)} />
              <DetailRow label="Narration" value={tx.narration} />
              <DetailRow
                label="Transfer Reference"
                value={tx.transferReference}
              />
              <DetailRow label="Batch Number" value={tx.batchNumber} />
            </dl>
          </SectionCard>

          <SectionCard title="Status Information" icon={ClockIcon}>
            <dl className="divide-y divide-gray-200">
              <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-4">
                <dt className="text-sm font-medium text-gray-500">
                  Process Status
                </dt>
                <dd className="mt-1 sm:col-span-2 sm:mt-0">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge(tx.processStatus)}`}
                  >
                    {tx.processStatus}
                  </span>
                </dd>
              </div>
              <DetailRow
                label="Notification Sent"
                value={
                  <span
                    className={`inline-flex items-center ${
                      tx.notificationSent
                        ? "text-green-600"
                        : "text-gray-400"
                    }`}
                  >
                    {tx.notificationSent ? (
                      <CheckCircleIcon className="h-4 w-4 mr-1" />
                    ) : (
                      <XCircleIcon className="h-4 w-4 mr-1" />
                    )}
                    {tx.notificationSent ? "Yes" : "No"}
                  </span>
                }
              />
              <DetailRow label="Error Message" value={tx.errorMessage} />
            </dl>
          </SectionCard>
        </div>

        {tx.id && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg px-6 py-4">
            <p className="text-xs text-gray-500 font-mono break-all">
              Transaction ID: {tx.id}
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
